"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ChevronRight, CircleAlert } from 'lucide-react';
import { PostsProvider, usePosts, Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
};
function BlogPostsGrid() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm">
            <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
            <CardHeader className="space-y-3">
              <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
                <div className="h-4 w-2/3 bg-muted animate-pulse rounded" />
              </div>
            </CardContent>
            <CardFooter>
              <div className="h-4 w-24 bg-muted animate-pulse rounded" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <CircleAlert className="size-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold font-default mb-2">Failed to load posts</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          We encountered an error while trying to fetch the latest blog posts. Please try again later.
        </p>
        <Button onClick={refetch} variant="outline">
          Retry
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 border border-dashed border-border rounded-xl">
        <p className="text-muted-foreground">No blog posts found.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((post: Post, i: number) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full bg-card">
            {post.featuredImage && <div className="overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage} alt={`Blog post: ${post.title}${(post as any).service ? ` about ${(post as any).service}` : ''}`} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </AspectRatio>
              </div>}
            
            <CardHeader className="pb-4 flex-none">
              <div className="flex items-center justify-between mb-3">
                {post.categories && post.categories.length > 0 && <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-transparent">
                    {post.categories[0].name}
                  </Badge>}
                <div className="flex items-center text-xs text-muted-foreground">
                  <SafeHydrate><Calendar className="size-3 mr-1.5" /></SafeHydrate>
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                </div>
              </div>
              <h3 className="text-xl font-semibold font-default leading-tight group-hover:text-primary transition-colors line-clamp-2">
                {post.link ? <Link to={post.link} className="focus:outline-none">
                    {post.title}
                  </Link> : post.title}
              </h3>
            </CardHeader>
            
            <CardContent className="flex-grow">
              {post.excerpt && <div className="text-muted-foreground text-sm line-clamp-3" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </CardContent>
            
            <CardFooter className="pt-0 flex-none">
              {post.link && <Link to={post.link} className="inline-flex items-center text-sm font-medium text-secondary hover:text-secondary/80 transition-colors">
                  Read More
                  <ChevronRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>}
            </CardFooter>
          </Card>)}
      </div>

      {totalPages > 1 && <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button variant="ghost" size="default" onClick={prevPage} disabled={!hasPrev} className="gap-1 pl-2.5">
                <ChevronRight className="size-4 rotate-180" />
                <span>Previous</span>
              </Button>
            </PaginationItem>
            
            <PaginationItem className="hidden sm:flex items-center px-4 text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </PaginationItem>

            <PaginationItem>
              <Button variant="ghost" size="default" onClick={nextPage} disabled={!hasNext} className="gap-1 pr-2.5">
                <span>Next</span>
                <ChevronRight className="size-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function BlogPostsList() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="blog-posts-list" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-12 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Latest Industry Insights
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg">
            Stay updated with the latest news, tips, and expert advice on roofing and exterior maintenance.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <BlogPostsGrid />
        </PostsProvider>
      </div>
    </section>;
}